import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Box, Fade, IconButton, Typography, Tooltip } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useAtom } from 'jotai';
import { useLocation } from 'react-router-dom';
import useInfiniteQuery from '../../../../Hooks/useInfiniteQuery';
import useAPI from '../../../../api';
import LoadListItem from './LoadListItem';
import SectionPage from '../../../../Components/Paper/SectionPage';
import LoadDialog from './LoadDialog';
import { LOADS_FILTERS, LOADS_SORTINGS } from '../../Utils/constants';
import { searchValuesAtom } from '../../../../Atoms';
import ImportsFiltersWidget from './ImportsFiltersWidget';
import mapFilters from '../../../../Helpers/Filters/mapFilters';
import EmptyInfiniteListComponent from '../../../../Components/ContactListWrapper/EmptyInfiniteListComponent';
import saveFiltersInLocal from '../../../../Helpers/Filters/saveFiltersInLocal';

const renderLoadItem = ({
  id,
  fileName,
  userFullName,
  isSuccessful: isSuccessFull,
  isFileDeleted,
  completedOn,
  createdOn,
  sectionName,
  errors,
}) => (
  <LoadListItem
    id={id}
    fileName={fileName}
    userFullName={userFullName}
    isSuccessFull={isSuccessFull}
    isFileDeleted={isFileDeleted}
    completedOn={completedOn}
    createdOn={createdOn}
    sectionName={sectionName}
    errors={errors}
  />
);

const LoadsHistory = function LoadsHistory({ show = false, close = () => {} }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const API = useAPI();
  const location = useLocation()?.pathname;

  const [reload, setReload] = useState(0);
  const [sections, setSections] = useState([]);
  const [loadDialog, setLoadDialog] = useState(false);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const [campaigns, setCampaigns] = useState([]);

  const handleOpenLoadDialog = () => {
    setLoadDialog(true);
  };
  const handleCloseLoadDialog = () => {
    setLoadDialog(false);
  };

  const handleConfirmFilters = (newFilters = {}) => {
    setSearchValues((prev) => ({ ...prev, ...newFilters }));
  };

  useEffect(() => {
    API.getWaveSections(
      (res) => {
        setSections(res?.filter(({ isSystem }) => !isSystem));
      },
      () => {
        setSections([]);
      }
    );
  }, []);

  useEffect(() => {
    API.getCommercialCampaignsHistory(
      {
        pageNumber: 1,
        pageSize: 100,
        silent: true,
        filterExpression: mapFilters(
          [],
          [
            {
              keyName: 'isActive',
              operator: 'eq',
            },
          ],
          [],
          { isActive: true }
        ),
        sortExpression: {
          sortValue: 'startDate',
          sort: 'desc',
        },
      },
      (res) => {
        setCampaigns(res);
      }
    );
  }, []);

  const getLoadsHistory = useCallback(
    (pageNumber) =>
      show
        ? API.getImportHistory(
            {
              pageNumber,
              silent: true,
              filterExpression: mapFilters(
                ['fileName'],
                LOADS_FILTERS,
                [],
                searchValues
              ),
              sortExpression: searchValues?.sortValue
                ? {
                    sortValue: searchValues?.sortValue.value,
                    sort: searchValues?.sortValue.direction,
                  }
                : null,
            },
            () => {
              saveFiltersInLocal(searchValues, location);
            }
          )
        : Promise.resolve([]),
    [searchValues, show, reload]
  );

  const { ObserverWrapper } = useInfiniteQuery(getLoadsHistory);
  return (
    <Fade in={show}>
      <div style={{ display: show ? 'block' : 'none', height: '100%' }}>
        <LoadDialog
          open={loadDialog}
          onClose={handleCloseLoadDialog}
          sections={sections}
          campaigns={campaigns}
          reloadHistory={() => setReload((prev) => prev + 1)}
        />
        <SectionPage
          onClickFab={handleOpenLoadDialog}
          sortingOptions={LOADS_SORTINGS}
          hideExportExcel
          sectionSubName={
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
                marginTop: '7%',
                marginLeft: '-9px',
                alignItems: 'center',
              }}
            >
              <Tooltip title={intl.messages.BACK} placement="bottom">
                <IconButton id="back-icnBtn" onClick={close}>
                  <ArrowBackRoundedIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="overline" sx={{ color: '#5A5A5A' }}>
                {t('FILES')}
              </Typography>
            </Box>
          }
          sectionName="LOADS_HISTORY"
          fab={t('LOAD_NEW')}
          fabVariant="extended"
          ListObserverWrapper={ObserverWrapper}
          customEntryFunction={renderLoadItem}
          CustomEmptyList={EmptyInfiniteListComponent}
          customFilters={
            <ImportsFiltersWidget
              sections={sections}
              defaultValues={searchValues}
              onConfirm={handleConfirmFilters}
            />
          }
        />
      </div>
    </Fade>
  );
};

LoadsHistory.propTypes = {
  show: PropTypes.bool,
  close: PropTypes.func,
};

export default LoadsHistory;
